import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { t } from '@/i18n'
import { getActivityEvents, type ActivityOut } from '@/lib/api'
import { formatDateTime } from '@/lib/format'

const KIND_KEY: Record<string, string> = {
  'ui.click': 'act.click',
  'ui.navigate': 'act.nav',
  'ui.submit': 'act.form',
  'ui.api_error': 'act.apiErr',
  'ui.js_error': 'act.jsErr',
}

export function Activity() {
  const [errorsOnly, setErrorsOnly] = useState(false)
  const events = useQuery({
    queryKey: ['activity', errorsOnly],
    queryFn: () => getActivityEvents(200, errorsOnly),
    refetchInterval: 8_000,
  })

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 md:px-8 md:py-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">{t('act.title')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('act.lead')}</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-400">
          <input
            type="checkbox"
            checked={errorsOnly}
            onChange={(event) => setErrorsOnly(event.target.checked)}
          />
          {t('act.errorsOnly')}
        </label>
      </div>
      {events.isLoading && <p className="mt-6 text-sm text-slate-500">{t('common.loading')}</p>}
      {events.isError && (
        <p className="mt-6 text-sm text-loss">{events.error instanceof Error ? events.error.message : t('common.error')}</p>
      )}
      {events.data && events.data.length === 0 && (
        <p className="mt-6 text-sm text-slate-500">{t('act.empty')}</p>
      )}
      {events.data && events.data.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-slate-500">
              <tr>
                <th className="py-2 pr-3">{t('act.time')}</th>
                <th className="py-2 pr-3">{t('act.who')}</th>
                <th className="py-2 pr-3">{t('act.action')}</th>
                <th className="py-2 pr-3">{t('act.screen')}</th>
                <th className="py-2 pr-3">{t('act.label')}</th>
                <th className="py-2">{t('act.detail')}</th>
              </tr>
            </thead>
            <tbody>
              {events.data.map((row) => (
                <ActivityRow key={row.id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function ActivityRow({ row }: { row: ActivityOut }) {
  const after = row.after ?? {}
  const kindKey = KIND_KEY[row.action]
  const kind = kindKey ? t(kindKey) : row.action
  const path = typeof after.path === 'string' ? after.path : '—'
  const label = typeof after.label === 'string' ? after.label : ''
  const status = typeof after.status === 'number' ? after.status : null
  const message = typeof after.message === 'string' ? after.message : ''
  const requestId = row.request_id ?? (typeof after.request_id === 'string' ? after.request_id : '')
  const isError = row.action.includes('error')
  return (
    <tr className="border-t border-surface-border/60 align-top">
      <td className="num whitespace-nowrap py-2 pr-3 text-slate-400">{formatDateTime(row.created_at)}</td>
      <td className="py-2 pr-3 text-slate-500">{row.actor_label}</td>
      <td className={`py-2 pr-3 ${isError ? 'text-loss' : 'text-slate-200'}`}>{kind}</td>
      <td className="py-2 pr-3 text-slate-400">{path}</td>
      <td className="py-2 pr-3 text-slate-200">{label || '—'}</td>
      <td className="py-2 text-slate-400">
        {status != null ? <span className="num">{status} </span> : null}
        {message}
        {requestId ? <span className="ml-1 text-xs text-slate-600">{requestId}</span> : null}
      </td>
    </tr>
  )
}
